// Components
import {
  IDynamicQuery,
  IQueryArguments,
  IContent,
  IContentType,
  IUser,
  IFilterOption,
  IFlight,
} from "@vmosolutions/oc-interfaces";
import { IDaysOfWeekOption } from "./Helpers";

/*
enum Condition {
  equal = "EQ",
  greaterThan = "GT",
  greaterThanOrEqual = "GTE",
  lessThan = "LT",
  lessThanOrEqual = "LTE",
  between = "BT",
  notEqual = "NE",
  arrayEmpty = "ARRAY_EMPTY",
}
*/

/**
 * for content management
 * Parse the current search filter format into the accepted query format
 */
export const convertFilter2Query = (searchFilter: any, isFlight?: boolean): IDynamicQuery => {
  //input (Filter Search format) = { value: "f_flightNumber", label: "Flight #", type: "flightEvent", searchValue: ["908", "112"]}
  //output (IDynamicQuery format) = { field: "flightNumber", condition: "EQ", value: [908, 112] };

  // Filters are reduced so that they are only returned if they contain valid values.
  const newFilters = searchFilter.reduce((queryFilters: IQueryArguments[], filter: any) => {
    const fieldName = filter.value.replace("c_", "");
    if (!filter.searchValue) {
      return null;
    }
    const queryObj: IQueryArguments = { field: fieldName, condition: "EQ", value: null };

    switch (fieldName) {
      case "source":
        queryObj.condition = "REGEX";
        queryObj.value = filter.searchValue;
        queryObj.field = "source.name";
        break;
      case "fileName":
        queryObj.value = filter.searchValue;
        queryObj.field = "fileName";
        break;
      case "fileType":
        //handle either txt or .txt as the search value
        queryObj.value = filter.searchValue.startsWith(".")
          ? [filter.searchValue.toLowerCase()]
          : [".".concat(filter.searchValue.toLowerCase())];
        break;
      case "efbMadeAvailAt":
        queryObj.field = "efb.MadeAvailAt";
        queryObj.condition = "GT";
        queryObj.value = filter.searchValue;
        break;
      case "associatedFlights":
        queryObj.field = "associatedFlights._id";
        queryObj.condition = "EQ";
        queryObj.value = filter.searchValue?.length ? filter.searchValue.map((flights: IFlight) => flights?._id) : [];
        break;
      case "uploadedBy":
        queryObj.field = fieldName;
        queryObj.value = filter.searchValue.map((user: IUser) => user._id);
        break;
      case "contentType":
        queryObj.value = filter?.searchValue?.map((contentType: IContentType) => contentType.title);
        queryObj.field = "contentType.title";
        break;
      case "logSheetNumber":
        queryObj.value = filter?.searchValue;
        queryObj.field = "fields.logSheetNumber";
        break;
      case "reviewStatus":
        if (filter?.searchValue === "notreviewed") {
          queryFilters.push({ field: "validationHistory", condition: "ARRAY_EMPTY", value: "true" });
        } else {
          queryFilters.push({ field: "validationHistory", condition: "ARRAY_EMPTY", value: "false" });
        }
        if (filter?.searchValue === "valid") {
          queryObj.field = "isValid";
          queryObj.condition = "EQ";
          queryObj.value = "true";
        } else if (filter?.searchValue === "errors") {
          queryObj.field = "isValid";
          queryObj.condition = "EQ";
          queryObj.value = "false";
        }
        break;
      case "aircraft":
        queryObj.field = "associatedFlights.aircraft._id";
        queryObj.value = filter?.searchValue?.map((id: string) => (id === "unassigned" ? null : id));
        break;
      case "createdAt":
      case "modifiedAt": {
        const from = filter?.searchValue?.minTime ? new Date(filter.searchValue.minTime)?.valueOf() : null;
        const to = filter?.searchValue?.maxTime ? new Date(filter.searchValue.maxTime)?.valueOf() : null;
        if (!!from && !!to) {
          queryObj.condition = "BT";
          queryObj.value = [from, to];
        } else if (!!from && !to) {
          queryObj.condition = "GT";
          queryObj.value = from;
        } else {
          queryObj.condition = "LT";
          queryObj.value = to;
        }
        // If supplied, apply selected days in it's rrule format.
        const byDays = filter?.searchValue?.daysOfWeek;
        const rRule: string =
          Array.isArray(byDays) && byDays.length > 0
            ? `BYDAY=${byDays.map((option: IDaysOfWeekOption) => option.rrule).toString()};`
            : null;
        if (rRule) {
          queryObj.rrule = rRule;
        }
        break;
      }
      default:
    }
    if (queryObj?.value) {
      queryFilters.push(queryObj);
    }
    return queryFilters;
  }, []);
  return {
    queryArgs: newFilters,
    returnFields: [
      // Return all fields
      "_id",
      "contentType",
      "fileName",
      "uploadedBy",
      "fileType",
      "fileSize",
      "submittedByIP",
      // "fields", - this could have private fields so don't return it
      "source",
      "associatedFlights",
      "associatedBookings",
      "createdAt",
      "createdBy",
      "modifiedAt",
      "modifiedBy",
      "isValid",
      "validation",
      "validationHistory",
      "displayTitle",
      "efb",
    ],
  };
};

// Reduce the filters array to an objects to simplify filtering the dataset.
export const reducedFilter = (selectedFilter: any, allowMultiFilter: boolean): any => {
  const reducedFilter = selectedFilter.reduce((map: any, filter: IFilterOption) => {
    const { value, searchValue } = filter;
    const key = value.replace("c_", "");
    switch (true) {
      case Array.isArray(searchValue) && searchValue.length > 0: // Array with Value
        map[key] = searchValue;
        break;
      case !!searchValue &&
        !Array.isArray(searchValue) &&
        searchValue.hasOwnProperty("minTime") &&
        searchValue.hasOwnProperty("maxTime"): // Date Range
        if (!!searchValue.minTime || !!searchValue.maxTime) {
          map[key] = searchValue;
        }
        break;
      case !!searchValue && !Array.isArray(searchValue): // Simple Value
        map[key] = searchValue;
        break;
      case !allowMultiFilter: // Used as single filter (almost makes value even when null)
        map[key] = searchValue;
        break;
      default:
        break;
    }
    return map;
  }, {});
  return reducedFilter;
};

/**
 * Accepts the user's input, the selected filter and the selected filter type and passes in the found flights
 * into an array and the first found flight into the callback function searchFlightByInput
 * which the callback function in the FlightTimeline.tsx will fire the jumpToInputtedFlight function
 * @param searchValue -
 * @param eventType - // used in the advanced filter search table
 */
export const filterEvents = (filters: any, events: IContent[], isFlight: boolean): IContent[] => {
  // Set default sets to their searched event list.
  let newFilteredEvents: IContent[] = events;

  // If there are no filters containing values, or no data to filter, return
  if (!filters || Object.keys(filters).length === 0 || newFilteredEvents.length === 0) {
    return [];
  }

  for (const key in filters) {
    const searchValue = filters[key];
    if (!key || !filters) {
      return [];
    }

    newFilteredEvents = newFilteredEvents.filter((item: IContent) => {
      switch (key) {
        case "uploadedBy": {
          const userIdArray = searchValue?.map((user: IUser) => user._id);
          return userIdArray?.includes(item[key]);
        }
        // CONTENT
        // switch (key) {
        //   case "":
        //     break
        //   default:
        //     return false;
        // }
        default:
          return item;
      }
    });
  }
  // update parent component.
  return newFilteredEvents;
};

export const filterContent = (filters: any, items: IContent[]): IContent[] => {
  // Set default sets to their searched event list.
  let newFilteredEvents: IContent[] = items;
  // If there are no filters containing values, or no data to filter, return
  if (!filters || Object.keys(filters).length === 0 || newFilteredEvents.length === 0) {
    return [];
  }
  for (const key in filters) {
    // const searchValue = filters[key];
    if (!key || !filters) {
      return [];
    }
    newFilteredEvents = newFilteredEvents.filter((item: IContent) => {
      return true;
    });
  }
  return newFilteredEvents;
};
